/** Bill checks on the record page: the advisory flags that cite this bill (feature 2).
 *
 * **Closed by default, and silent when there is nothing to say.** A record with no flag draws nothing; one
 * with flags draws a single line that says how many and opens to a list. The list is chips and buttons, not
 * prose, so it stays inside the page's text budget, and the explanation (what a check is, that it never
 * edits a figure, who owns a missing bill) is behind the info control.
 *
 * Every flag is a pointer to two receipts (rule 3): the bill on this page and the one it was compared with,
 * each a button that opens its receipt, and a way to open the other bill's own page. Nothing here changes a
 * figure, writes anything or offers to dismiss one: the check is derived again on every read, and goes when
 * the books stop showing it.
 *
 * **Four outcomes of the read, as `AssuranceAbout` has them.** Not shown the page (404): nothing, since a
 * sentence would confirm a record exists outside an engagement. Answered and empty: nothing. Answered with
 * flags: the line and the list. Failed: said, with a retry, because a dropped request dressed as "no flags"
 * is the claim that every check ran and found nothing.
 */

import { useState } from "react";

import { useRecordBillChecks } from "../api/billChecks";
import type { BillCheck, BillCheckSide } from "../api/billChecks";
import { Disclosure, InfoPopover, StatusChip } from "../components/ui";
import { roundSignificant } from "../lib/decimal";
import { billCheckSentence, billCheckTitle } from "../lib/customerWords";
import { statedQuantity, unitLabel } from "../lib/format";
import { periodWords } from "./scopeTable/model";
import styles from "./RecordBillChecks.module.css";
import tableStyles from "./scopeTable/scopeTable.module.css";

/** A bill as a button label: its size and its period, which is what tells two bills apart. */
function billLabel(side: BillCheckSide): string {
  return `${statedQuantity(side.quantity)} ${unitLabel(side.unit)}, ${periodWords(side.period_start, side.period_end)}`;
}

function sentenceOf(flag: BillCheck, here: boolean): string {
  return billCheckSentence(flag.why, {
    here,
    times: flag.ratio ? (roundSignificant(flag.ratio, 2) ?? undefined) : undefined,
    unitPair: flag.compared ? [unitLabel(flag.subject.unit), unitLabel(flag.compared.unit)] : undefined,
    quietDays: flag.quiet_days ?? undefined,
  });
}

export function RecordBillChecks({
  recordId,
  onReceipt,
  onOpenRecord,
}: {
  recordId: string;
  onReceipt: (calculationId: string) => void;
  onOpenRecord: (recordId: string) => void;
}) {
  const read = useRecordBillChecks(recordId);
  const [open, setOpen] = useState(false);

  if (read.isPending) return null;
  if (read.error) {
    return (
      <div role="alert" data-testid="bill-checks-failed" className={styles.failed}>
        <span>Bill checks could not be read.</span>
        <button type="button" className={tableStyles.ghost} onClick={() => void read.refetch()}>
          Try again
        </button>
      </div>
    );
  }
  const items = read.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <Disclosure
      testId="record-bill-checks"
      tone="warn"
      open={open}
      onOpenChange={setOpen}
      summary={<b>{items.length === 1 ? "1 bill check" : `${String(items.length)} bill checks`}</b>}
      toggleLabel="advice about this bill, never a change to a figure"
    >
      <div className={styles.chips}>
        <InfoPopover infoKey="record.bill-checks" />
      </div>
      {open ? (
        <ul className={styles.list} data-testid="bill-check-list">
          {items.map((flag) => {
            const here = flag.subject.record_id === recordId;
            const mine = here ? flag.subject : (flag.compared ?? flag.subject);
            const other = here ? flag.compared : flag.subject;
            return (
              <li
                key={flag.key}
                className={styles.item}
                data-testid="bill-check"
                data-kind={flag.kind}
                data-why={flag.why}
              >
                <span className={styles.chips}>
                  <StatusChip tone="warn" size="sm">
                    {billCheckTitle(flag.kind)}
                  </StatusChip>
                  <StatusChip tone="neutral" size="sm">
                    {sentenceOf(flag, here)}
                  </StatusChip>
                </span>
                <span className={styles.actions}>
                  <button
                    type="button"
                    className={tableStyles.figureButton}
                    data-testid="bill-check-this"
                    aria-label={`Receipt for this bill: ${billLabel(mine)}`}
                    onClick={() => onReceipt(mine.calculation_id)}
                  >
                    {billLabel(mine)}
                  </button>
                  {other ? (
                    <>
                      <button
                        type="button"
                        className={tableStyles.figureButton}
                        data-testid="bill-check-other"
                        aria-label={`Receipt for the other bill: ${billLabel(other)}`}
                        onClick={() => onReceipt(other.calculation_id)}
                      >
                        {billLabel(other)}
                      </button>
                      <button
                        type="button"
                        className={tableStyles.ghost}
                        data-testid="bill-check-open-other"
                        onClick={() => onOpenRecord(other.record_id)}
                      >
                        Open the other bill
                      </button>
                    </>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </Disclosure>
  );
}
